import { Injectable, inject } from '@angular/core';
import { buildQueryFilter } from '@shared/helpers/build-query-filter';
import { User } from '@shared/models';
import { QueryOptions } from 'odata-query';
import { map, Observable, of, switchMap } from 'rxjs';
import {
  ClientAssignmentResponse,
  CountryMaterialResponse,
  CreateClientAssignmentRequest,
  CreateCountryMaterialRequest,
  GstarClient,
} from '../../../web-api-client';
import {
  mapClientAssignmentFromResponse,
  mapClientFromResponse,
  mapClientToCreateRequest,
  mapClientToUpdateRequest,
} from '../mappers';
import { mapCountryFromMaterialResponse } from '../mappers/country/map-country-from-material-response';
import { Client, ClientAssignment, Collection } from '../models';
import { CountryMaterial } from '../models/country-material';

@Injectable({
  providedIn: 'root',
})
export class ClientService {
  private readonly gstarClient = inject(GstarClient);

  get(query?: Partial<QueryOptions<Client>>): Observable<Collection<Client>> {
    return this.gstarClient
      .clientsGET(
        undefined,
        buildQueryFilter(query),
        query?.top?.toString(),
        query?.skip?.toString(),
        query?.orderBy?.toString(),
        query?.count?.toString(),
        undefined
      )
      .pipe(
        map(response => ({
          total: response.odataCount ?? 0,
          items:
            response.value?.map(response => mapClientFromResponse(response)) ??
            [],
        }))
      );
  }

  getById(clientId: number): Observable<Client> {
    return this.gstarClient
      .clientsGET2(clientId)
      .pipe(map(response => mapClientFromResponse(response)));
  }

  create(client: Client): Observable<Client> {
    return this.gstarClient
      .clientsPOST(mapClientToCreateRequest(client))
      .pipe(map(response => mapClientFromResponse(response)));
  }

  getClientAssignment(clientId: number): Observable<User[]> {
    return this.gstarClient
      .users(
        clientId,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined
      )
      .pipe(map(y => y.value?.map(x => new User(x)) ?? []));
  }

  getCountryAssignment(clientId: number): Observable<CountryMaterial[]> {
    return this.gstarClient
      .byclientAll(clientId)
      .pipe(map(y => y.map(x => mapCountryFromMaterialResponse(x)) ?? []));
  }

  addClientAssignment(
    clientId: number,
    userId: string
  ): Observable<ClientAssignment> {
    return this.gstarClient
      .clientassignmentsPOST(
        new CreateClientAssignmentRequest({
          clientId,
          userId,
          isActive: true,
        })
      )
      .pipe(
        map((response: ClientAssignmentResponse) =>
          mapClientAssignmentFromResponse(response)
        )
      );
  }

  removeClientAssignment(clientId: number, userId: string): Observable<void> {
    return this.gstarClient
      .clientassignmentsGET(
        undefined,
        this.getClientAssignmentFilter(clientId, userId),
        '1',
        undefined,
        undefined,
        undefined,
        undefined
      )
      .pipe(
        switchMap(response => {
          const clientAssignmentId =
            response?.value?.[0]?.clientAssignmentId ?? null;
          if (!clientAssignmentId) return of(void 0);

          return this.gstarClient.clientassignmentsDELETE(clientAssignmentId);
        })
      );
  }

  addCountryMaterial(
    clientId: number,
    countryId: number
  ): Observable<CountryMaterialResponse> {
    return this.gstarClient
      .countrymaterialPOST(
        new CreateCountryMaterialRequest({
          clientId,
          countryId,
          isActive: true,
        })
      )
      .pipe();
  }

  removeCountryMaterial(
    clientId: number,
    countryMaterialId: number
  ): Observable<void> {
    return this.gstarClient.delete(clientId, [countryMaterialId]).pipe();
  }

  update(client: Client): Observable<void> {
    return this.gstarClient.clientsPUT(
      client.clientId,
      mapClientToUpdateRequest(client)
    );
  }

  delete(clientId: number): Observable<void> {
    return this.gstarClient.clientsDELETE(clientId);
  }

  getClientAssignmentFilter = (clientId: number, userId: string): string =>
    `clientId eq ${clientId} and user/id eq '${userId}' and isActive eq true`;
}
